import { TargetingKeyMissingError, OpenFeatureEventEmitter, ProviderStatus, ProviderEvents, GeneralError, StandardResolutionReasons, TypeMismatchError, ParseError } from '@openfeature/server-sdk';
import { getClient } from 'configcat-js-ssr';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function contextValueToString(contextValue) {
    if (typeof contextValue === 'string') {
        return contextValue;
    }
    if (typeof contextValue === 'boolean' || typeof contextValue === 'number' || contextValue === null) {
        return String(contextValue);
    }
    if (typeof contextValue === 'undefined') {
        return contextValue;
    }
    if (contextValue instanceof Date) {
        return contextValue.toISOString();
    }
    return JSON.stringify(contextValue);
}
function transformContextValues(contextValue) {
    if (contextValue === null) {
        return {};
    }
    if (typeof contextValue !== 'object' || Array.isArray(contextValue)) {
        const value = contextValueToString(contextValue);
        return value ? { value } : {};
    }
    if (contextValue instanceof Date) {
        return { value: contextValue.toISOString() };
    }
    return Object.entries(contextValue).reduce((context, [key, value]) => {
        const transformedValue = contextValueToString(value);
        return transformedValue ? Object.assign(Object.assign({}, context), { [key]: transformedValue }) : context;
    }, {});
}
function stringOrUndefined(param) {
    if (typeof param === 'string') {
        return param;
    }
    return undefined;
}
function transformContext(context) {
    const { targetingKey, email, country } = context, attributes = __rest(context, ["targetingKey", "email", "country"]);
    if (!targetingKey) {
        throw new TargetingKeyMissingError('ConfigCat evaluation context can only be used if a targetingKey is given');
    }
    return {
        identifier: targetingKey,
        email: stringOrUndefined(email),
        country: stringOrUndefined(country),
        custom: transformContextValues(attributes),
    };
}

class ConfigCatProvider {
    constructor(...params) {
        this.events = new OpenFeatureEventEmitter();
        this._status = ProviderStatus.NOT_READY;
        this.metadata = {
            name: ConfigCatProvider.name,
        };
        this._clientParameters = params;
    }
    static create(...params) {
        return new ConfigCatProvider(...params);
    }
    get status() {
        return this._status;
    }
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                var _a;
                const originalParameters = this._clientParameters;
                (_a = originalParameters[2]) !== null && _a !== void 0 ? _a : (originalParameters[2] = {});
                const options = originalParameters[2];
                const oldSetupHooks = options.setupHooks;
                options.setupHooks = (hooks) => {
                    oldSetupHooks === null || oldSetupHooks === void 0 ? void 0 : oldSetupHooks(hooks);
                    // After resolving, once, we can simply emit events the next time
                    const onProviderReady = () => {
                        this.events.emit(ProviderEvents.Ready);
                        this._status = ProviderStatus.READY;
                    };
                    hooks.once('clientReady', () => {
                        hooks.on('clientReady', onProviderReady);
                        onProviderReady();
                        resolve();
                    });
                    hooks.on('configChanged', (projectConfig) => this.events.emit(ProviderEvents.ConfigurationChanged, {
                        flagsChanged: projectConfig ? Object.keys(projectConfig.settings) : undefined,
                    }));
                    hooks.on('clientError', (message, error) => {
                        this._status = ProviderStatus.ERROR;
                        this.events.emit(ProviderEvents.Error, {
                            message: message,
                            metadata: error,
                        });
                    });
                };
                this._client = getClient(...originalParameters);
            });
        });
    }
    get configCatClient() {
        return this._client;
    }
    onClose() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            (_a = this._client) === null || _a === void 0 ? void 0 : _a.dispose();
        });
    }
    resolveBooleanEvaluation(flagKey, defaultValue, context) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._client) {
                throw new GeneralError('Provider is not initialized');
            }
            const _a = yield this._client.getValueDetailsAsync(flagKey, undefined, context.targetingKey != null ? transformContext(context) : undefined), { value } = _a, evaluationData = __rest(_a, ["value"]);
            const validatedValue = validateFlagType('boolean', value);
            return validatedValue
                ? toResolutionDetails(validatedValue, evaluationData)
                : toResolutionDetails(defaultValue, evaluationData, StandardResolutionReasons.DEFAULT);
        });
    }
    resolveStringEvaluation(flagKey, defaultValue, context) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._client) {
                throw new GeneralError('Provider is not initialized');
            }
            const _a = yield this._client.getValueDetailsAsync(flagKey, undefined, context.targetingKey != null ? transformContext(context) : undefined), { value } = _a, evaluationData = __rest(_a, ["value"]);
            const validatedValue = validateFlagType('string', value);
            return validatedValue
                ? toResolutionDetails(validatedValue, evaluationData)
                : toResolutionDetails(defaultValue, evaluationData, StandardResolutionReasons.DEFAULT);
        });
    }
    resolveNumberEvaluation(flagKey, defaultValue, context) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._client) {
                throw new GeneralError('Provider is not initialized');
            }
            const _a = yield this._client.getValueDetailsAsync(flagKey, undefined, context.targetingKey != null ? transformContext(context) : undefined), { value } = _a, evaluationData = __rest(_a, ["value"]);
            const validatedValue = validateFlagType('number', value);
            return validatedValue
                ? toResolutionDetails(validatedValue, evaluationData)
                : toResolutionDetails(defaultValue, evaluationData, StandardResolutionReasons.DEFAULT);
        });
    }
    resolveObjectEvaluation(flagKey, defaultValue, context) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._client) {
                throw new GeneralError('Provider is not initialized');
            }
            const _a = yield this._client.getValueDetailsAsync(flagKey, undefined, context.targetingKey != null ? transformContext(context) : undefined), { value } = _a, evaluationData = __rest(_a, ["value"]);
            if (typeof value === 'undefined') {
                return toResolutionDetails(defaultValue, evaluationData, StandardResolutionReasons.DEFAULT);
            }
            if (!isType('string', value)) {
                throw new TypeMismatchError(`Requested object flag but the actual value is not a JSON string`);
            }
            try {
                const object = JSON.parse(value);
                if (typeof object !== 'object') {
                    throw new TypeMismatchError(`Requested object flag but the actual value is ${typeof value}`);
                }
                return toResolutionDetails(object, evaluationData);
            }
            catch (e) {
                if (e instanceof TypeMismatchError) {
                    throw e;
                }
                throw new ParseError(`Unable to parse '${value}' as JSON`);
            }
        });
    }
}
function toResolutionDetails(value, data, reason) {
    var _a;
    const matchedTargeting = 'matchedEvaluationRule' in data ? data.matchedEvaluationRule : data.matchedTargetingRule;
    const matchedPercentage = 'matchedEvaluationPercentageRule' in data ? data.matchedEvaluationPercentageRule : data.matchedPercentageOption;
    const matchedRule = Boolean(matchedTargeting || matchedPercentage);
    const evaluatedReason = matchedRule ? StandardResolutionReasons.TARGETING_MATCH : StandardResolutionReasons.STATIC;
    return {
        value,
        reason: reason !== null && reason !== void 0 ? reason : evaluatedReason,
        errorMessage: data.errorMessage,
        variant: (_a = data.variationId) !== null && _a !== void 0 ? _a : undefined,
    };
}
function isType(type, value) {
    return typeof value === type;
}
function validateFlagType(type, value) {
    if (typeof value !== 'undefined' && !isType(type, value)) {
        throw new TypeMismatchError(`Requested ${type} flag but the actual value is ${typeof value}`);
    }
    return value;
}

export { ConfigCatProvider };
